import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import GenericModal from '../../../../modal/generic_modal';
import * as tracking from '../../../../../tracking';
import PredictedEndDateModalChartSection from './PredictedEndDateModalChartSection';
import {ChartStatsContainer, PredictionInfo} from './PredictedEndDateModal.styled';
import PredictedEndDateModalStatsSection from './PredictedEndDateModalStatsSection';
import {ForecastTooltipFormulaRenderer} from '../../../../../components/ForecastTooltipFormulaRenderer';
import {convertStatsToMapPerDay, shouldShowReestimationWarning} from './PredictedEndDateModalLogic';
import {PROJECT_STATUS} from '../../../../../constants';

const PredictedEndDateModal = ({viewer, project, endDateInfo, closeModal}) => {
	const getContent = () => {
		const projectDone = project.status === PROJECT_STATUS.DONE;

		const totalTaskEstimate = project.projectHealth.totalTaskEstimate;
		const statsPerDayMap = convertStatsToMapPerDay(viewer.project.projectStats);
		const showOutOfResourcesWarning = project.projectHealth.predictedAverageScopePerDay !== null;
		if (showOutOfResourcesWarning) {
			tracking.trackEvent('Out of resources warning shown');
		}
		const showReestimationWarning = shouldShowReestimationWarning(totalTaskEstimate, statsPerDayMap, projectDone);
		if (showReestimationWarning) {
			tracking.trackEvent('Reestimation warning shown');
		}

		return (
			<>
				<PredictionInfo>{endDateInfo}</PredictionInfo>
				{showOutOfResourcesWarning ? (
					<PredictionInfo>
						The project does not have enough resources allocated to finish according to the prediction. The model
						assumes that your average cadence will remain unchanged until the project finishes.
					</PredictionInfo>
				) : null}
				{showReestimationWarning ? (
					<PredictionInfo>
						Recently, your project has been significantly reestimated. This has a negative impact on the accuracy of
						the prediction. For each day you work with your new estimates, the accuracy will improve.
					</PredictionInfo>
				) : null}
				<ChartStatsContainer>
					<PredictedEndDateModalStatsSection project={project} />
					<PredictedEndDateModalChartSection
						project={project}
						statsPerDayMap={statsPerDayMap}
						projectDone={projectDone}
					/>
				</ChartStatsContainer>
			</>
		);
	};

	const tooltipContent = () => {
		return (
			<ForecastTooltipFormulaRenderer
				items={[
					{
						title: 'Predicted project end is based on remaining work and its comparison to your team members future allocation, modified by their recent performance and focus.',
					},
					{
						title: `Since focus and performance are not static over a project's lifetime, you can also see the statistically probable early date of completion (Optimistic prediction) which assumes that focus and performance will be high, and the statstically probable late date of completion (Conservative prediction) which assumes that focus and performance will be low.`,
					},
				]}
				translatedMessage={true}
			/>
		);
	};

	return (
		<GenericModal
			closeModal={closeModal}
			content={getContent()}
			headerText={'Predicted project end'}
			headerInfo={tooltipContent()}
			modalWidth={900}
		/>
	);
};

const PredictedEndDateModalQuery = graphql`
	query PredictedEndDateModal_Query($projectId: ID) {
		viewer {
			component(name: "predicted_end_date_modal")
			actualPersonId
			id
			...PredictedEndDateModal_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {PredictedEndDateModalQuery};

export default createFragmentContainer(PredictedEndDateModal, {
	viewer: graphql`
		fragment PredictedEndDateModal_viewer on Viewer @argumentDefinitions(projectId: {type: "ID"}) {
			project(internalId: $projectId) {
				projectStats {
					id
					date
					totalEstimateMinutes
					totalBaselineEstimateMinutes
					totalDoneEstimateMinutes
					performance
					optimisticPerformance
					conservativePerformance
				}
			}
		}
	`,
	project: graphql`
		fragment PredictedEndDateModal_project on ProjectType {
			status
			projectHealth {
				totalTaskEstimate
				predictedAverageScopePerDay
			}
			...PredictedEndDateModalChartSection_project
			...PredictedEndDateModalStatsSection_project
		}
	`,
});
